import { createContext, useContext, useState, useCallback } from "react";
import { favoritesAPI } from "../api/favorites";

const FavoritesContext = createContext();

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error("useFavorites must be used within a FavoritesProvider");
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const [favoritesByType, setFavoritesByType] = useState({
    device: new Set(),
    organization: new Set(),
    dashboard: new Set(),
    geozone: new Set(),
  });
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState({
    device: false,
    organization: false,
    dashboard: false,
    geozone: false,
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Load favorite IDs for a specific entity type
  const loadFavoriteIds = useCallback(
    async (entityType) => {
      if (initialized[entityType]) return;

      try {
        setLoading(true);
        const response = await favoritesAPI.getFavoriteEntityIds(entityType);
        const favoriteIds = new Set(response.data);

        setFavoritesByType((prev) => ({
          ...prev,
          [entityType]: favoriteIds,
        }));

        setInitialized((prev) => ({
          ...prev,
          [entityType]: true,
        }));
      } catch (error) {
        console.error(`Failed to load ${entityType} favorites:`, error);
      } finally {
        setLoading(false);
      }
    },
    [initialized]
  );

  // Check if an entity is favorited
  const isFavorite = useCallback(
    (entityType, entityId) => {
      return favoritesByType[entityType].has(Number(entityId));
    },
    [favoritesByType]
  );

  // Toggle favorite status
  const toggleFavorite = useCallback(
    async (entityType, entityId) => {
      const numericId = Number(entityId);
      const wasIsFavorite = favoritesByType[entityType].has(numericId);

      // Optimistic update
      setFavoritesByType((prev) => {
        const newSet = new Set(prev[entityType]);
        if (wasIsFavorite) {
          newSet.delete(numericId);
        } else {
          newSet.add(numericId);
        }
        return {
          ...prev,
          [entityType]: newSet,
        };
      });

      try {
        const response = await favoritesAPI.toggle(entityType, entityId);
        const isNowFavorite = response.data;

        // Update with server response
        setFavoritesByType((prev) => {
          const newSet = new Set(prev[entityType]);
          if (isNowFavorite) {
            newSet.add(numericId);
          } else {
            newSet.delete(numericId);
          }
          return {
            ...prev,
            [entityType]: newSet,
          };
        });

        return isNowFavorite;
      } catch (error) {
        // Revert optimistic update on error
        setFavoritesByType((prev) => {
          const newSet = new Set(prev[entityType]);
          if (wasIsFavorite) {
            newSet.add(numericId);
          } else {
            newSet.delete(numericId);
          }
          return {
            ...prev,
            [entityType]: newSet,
          };
        });

        console.error("Failed to toggle favorite:", error);
        throw error;
      }
    },
    [favoritesByType]
  );

  // Get count of favorites by type
  const getFavoriteCount = useCallback(
    (entityType) => {
      return favoritesByType[entityType].size;
    },
    [favoritesByType]
  );

  // Clear favorites for a specific type (useful for logout/user change)
  const clearFavorites = useCallback((entityType = null) => {
    if (entityType) {
      setFavoritesByType((prev) => ({
        ...prev,
        [entityType]: new Set(),
      }));
      setInitialized((prev) => ({
        ...prev,
        [entityType]: false,
      }));
    } else {
      setFavoritesByType({
        device: new Set(),
        organization: new Set(),
        dashboard: new Set(),
        geozone: new Set(),
      });
      setInitialized({
        device: false,
        organization: false,
        dashboard: false,
        geozone: false,
      });
    }
  }, []);

  // Reload favorites for a specific type (forces refresh from server)
  const reloadFavorites = useCallback(async (entityType) => {
    try {
      setLoading(true);
      const response = await favoritesAPI.getFavoriteEntityIds(entityType);
      const favoriteIds = new Set(response.data);

      setFavoritesByType((prev) => ({
        ...prev,
        [entityType]: favoriteIds,
      }));

      setInitialized((prev) => ({
        ...prev,
        [entityType]: true,
      }));
    } catch (error) {
      console.error(`Failed to reload ${entityType} favorites:`, error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reload all favorites (forces refresh from server for all entity types)
  const reloadAllFavorites = useCallback(async () => {
    const entityTypes = ["device", "organization", "dashboard", "geozone"];

    try {
      setLoading(true);
      const promises = entityTypes.map(async (entityType) => {
        try {
          const response = await favoritesAPI.getFavoriteEntityIds(entityType);
          const favoriteIds = new Set(response.data);
          return { entityType, favoriteIds };
        } catch (error) {
          console.error(`Failed to reload ${entityType} favorites:`, error);
          return { entityType, favoriteIds: new Set() };
        }
      });

      const results = await Promise.all(promises);

      // Update all entity types at once
      const newFavoritesByType = {};
      const newInitialized = {};

      results.forEach(({ entityType, favoriteIds }) => {
        newFavoritesByType[entityType] = favoriteIds;
        newInitialized[entityType] = true;
      });

      setFavoritesByType((prev) => ({
        ...prev,
        ...newFavoritesByType,
      }));

      setInitialized((prev) => ({
        ...prev,
        ...newInitialized,
      }));

      // Trigger refresh for components that depend on favorites
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to reload favorites:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const value = {
    favoritesByType,
    setFavoritesByType,
    loading,
    initialized,
    loadFavoriteIds,
    isFavorite,
    toggleFavorite,
    getFavoriteCount,
    clearFavorites,
    reloadFavorites,
    reloadAllFavorites,
    refreshTrigger,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};
